package io.horion.auth.api.services

import io.horion.auth.api.models.Endpoint
import io.horion.auth.api.models.domain.LoginMethod
import io.horion.auth.api.models.domain.RegisterRequest
import io.horion.auth.api.utils.ApiClient.client
import io.horion.auth.api.models.domain.ForgotPasswordRequest
import io.horion.auth.api.models.domain.LoginRequest
import io.horion.auth.api.models.domain.LoginWithEmail
import io.horion.auth.api.models.domain.LoginWithPhone
import io.horion.auth.api.models.domain.ResetPasswordRequest
import io.horion.auth.api.models.domain.Send2FaOtpRequest
import io.horion.auth.api.models.domain.UserProfile
import io.horion.auth.api.models.domain.Verify2FaOtpRequest
import io.horion.auth.api.models.response.LoginResponse
import io.ktor.client.call.body
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import io.horion.auth.api.models.response.AuthResponse
import io.horion.auth.api.models.response.Response
import io.horion.auth.api.utils.Constants.API_AUTH_URL
import io.horion.auth.domain.models.SsoCodeResponse
import io.horion.auth.utils.LocalStorageManager
import io.horion.auth.utils.UserEnvironmentHelper
import io.ktor.client.request.accept
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.client.request.parameter
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import org.w3c.fetch.INCLUDE
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import kotlin.coroutines.cancellation.CancellationException
import kotlin.js.json


@Serializable
data class AccountDto(
    val userUuid: String,
    val email: String? = null,
    val phone: String? = null,
    val role: String? = null,
    val canAccessService: Boolean = true,
    val isActive: Boolean = false
)

@Serializable
data class CheckSessionResponseDto(
    val connected: Boolean = false,
    val accountsCount: Int = 0,
    val accounts: List<AccountDto> = emptyList(),
    val activeUserUuid: String? = null
)

@Serializable
data class ApiResponseDto(
    val success: Boolean = true,
    val message: String? = null
)
class AuthService {
    suspend fun checkSession(serviceName: String? = null): CheckSessionResponseDto {
        return try {
            val response = client.get("$API_AUTH_URL${Endpoint.CheckSession.path}") {
                accept(ContentType.Application.Json)
                serviceName?.let { parameter("service", it) }
            }

            if (response.status == HttpStatusCode.OK) {
                response.body<CheckSessionResponseDto>()

            } else {
                CheckSessionResponseDto(connected = false, accounts = emptyList())
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            CheckSessionResponseDto(connected = false, accounts = emptyList())
        }
    }

    suspend fun switchAccount(userUuid: String): ApiResponseDto {
        return try {
            val response = client.post("$API_AUTH_URL${Endpoint.SwitchAccount.path}") {
                accept(ContentType.Application.Json)
                parameter("userUuid", userUuid)
            }

            if (response.status == HttpStatusCode.OK) {
                ApiResponseDto(success = true, message = "Compte activé avec succès")
            } else {
                val errorBody = runCatching { response.body<Map<String, String>>() }.getOrNull()
                ApiResponseDto(
                    success = false,
                    message = errorBody?.get("error") ?: "Échec du changement de compte"
                )
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            ApiResponseDto(success = false, message = "Erreur réseau : ${e.message}")
        }
    }

    suspend fun logoutAccount(userUuid: String? = null): ApiResponseDto {
        return try {
            val response = client.get("$API_AUTH_URL${Endpoint.Logout.path}") {
                accept(ContentType.Application.Json)
                userUuid?.let { parameter("userUuid", it) }
            }

            if (response.status == HttpStatusCode.OK) {
                ApiResponseDto(success = true, message = "Déconnexion réussie")
            } else {
                val errorBody = runCatching { response.body<Map<String, String>>() }.getOrNull()
                ApiResponseDto(
                    success = false,
                    message = errorBody?.get("error") ?: "Échec de la déconnexion"
                )
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            ApiResponseDto(success = false, message = "Erreur réseau : ${e.message}")
        }
    }

    suspend fun send2FaOtp(channelValue: String, method : String) : Response {
        val response: HttpResponse = client.post("$API_AUTH_URL${Endpoint.DoubleFaSend.path}?method=$method") {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(Send2FaOtpRequest(channelValue))
        }
        val responseText = response.bodyAsText()
        return when (response.status) {
            HttpStatusCode.OK,
            HttpStatusCode.Created,
            HttpStatusCode.Accepted,
            HttpStatusCode.NoContent -> {
                val res = Json.decodeFromString<Success>(responseText)
                Response(success = true, message = res.message)
            }
            else -> {
                val res = Json.decodeFromString<Error>(responseText)
                Response(success = false, message = res.error)
            }
        }
    }

    suspend fun verify2FaAndEnable(channelValue: String,method: String,code: String) : Response {
        val response: HttpResponse = client.post("$API_AUTH_URL${Endpoint.DoubleFaVerify.path}?method=$method") {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(Verify2FaOtpRequest(channelValue,code))
        }
        val responseText = response.bodyAsText()
        return when (response.status) {
            HttpStatusCode.OK,
            HttpStatusCode.Created,
            HttpStatusCode.Accepted,
            HttpStatusCode.NoContent -> {
                val res = Json.decodeFromString<Success>(responseText)
                Response(success = true, message = res.message)
            }
            else -> {
                val res = Json.decodeFromString<Error>(responseText)
                Response(success = false, message = res.error)
            }
        }
    }

    suspend fun disable2FA() : Response {
        val response: HttpResponse = client.post("$API_AUTH_URL${Endpoint.DoubleFaDisable.path}") {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)

        }
        val responseText = response.bodyAsText()
        return when (response.status) {
            HttpStatusCode.OK,
            HttpStatusCode.Created,
            HttpStatusCode.Accepted,
            HttpStatusCode.NoContent -> {
                val res = Json.decodeFromString<Success>(responseText)
                Response(success = true, message = res.message)
            }
            else -> {
                val res = Json.decodeFromString<Error>(responseText)
                Response(success = false, message = res.error)
            }
        }
    }

    suspend fun generateCodeForMobile() : SsoCodeResponse? {
        val response = client.get("$API_AUTH_URL${Endpoint.Authorize.path}") {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
        }
        val responseText = response.bodyAsText()

        return when (response.status) {
            HttpStatusCode.OK,
            HttpStatusCode.Created,
            HttpStatusCode.Accepted,
            HttpStatusCode.NoContent -> {
                val res = Json.decodeFromString<SsoCodeResponse>(responseText)
                res
            }
            else -> {
                null
            }
        }

    }

    suspend fun register(request: RegisterRequest): Response {
        return try {
            val response = client.post("$API_AUTH_URL${Endpoint.Register.path}") {
                contentType(ContentType.Application.Json)
                accept(ContentType.Application.Json)
                setBody(request)
            }

            when (response.status) {
                HttpStatusCode.OK, HttpStatusCode.Created -> {
                    //val body = response.body<Map<String, String>>()
                    Response(true, "Inscription réussie")
                }

                HttpStatusCode.BadRequest -> {
                    val body = response.body<Map<String, String>>()
                    Response(false,body["error"] ?: "Données invalides")
                }

                HttpStatusCode.Conflict -> {
                    val body = response.body<Map<String, String>>()
                    Response(false,body["error"] ?: "Utilisateur déjà existant")
                }

                HttpStatusCode.TooManyRequests -> {
                    Response(false,"Trop de tentatives. Veuillez réessayer plus tard")
                }

                else -> {
                    Response(false,"Erreur serveur: ${response.status.value}")
                }
            }
        } catch (e: Exception) {
            Response(false,"Erreur de connexion")
        }
    }


    suspend fun login(request : LoginRequest,isMobileClient: Boolean) : Response {
        return try {
            val methods = when (request) {
                is LoginWithEmail -> LoginMethod.EMAIL
                is LoginWithPhone -> LoginMethod.PHONE
                else -> return Response(false,"Méthode de connexion invalide")
            }

            val response = client.post("$API_AUTH_URL${Endpoint.Login.path}?method=${methods.name}") {
                contentType(ContentType.Application.Json)
                accept(ContentType.Application.Json)
                if (isMobileClient){
                    headers {
                        append("X-Client-Type", "MOBILE")
                    }
                }
                setBody(request)
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    if (isMobileClient) {
                        val body = response.body<Map<String, String>>()
                        Response(true,body["code"] ?: "Invalid")
                    } else {
                        val body = response.body<Map<String, String>>()
                        val userUuid = body["status"] ?: "Invalid"
                        val role = body["role"] ?: "Invalid"
                        val isTwoFactorEnabled = body["isTwoFactorEnabled"]?.toBooleanStrictOrNull() ?: false

                        // ── Métriques récupérées instantanément depuis le navigateur ──────────────────
                        val userTimezone = UserEnvironmentHelper.getLocalTimezone()
                        val countryCode = UserEnvironmentHelper.getCountryCodeFromBrowser()
                        val countryName = UserEnvironmentHelper.getCountryNameFromCode(countryCode)
                        val inferredCity = UserEnvironmentHelper.inferCityFromTimezone(userTimezone)

                        val profile = UserProfile(
                            id = userUuid,
                            role = role,
                            email = (request as? LoginWithEmail)?.email,
                            phone = (request as? LoginWithPhone)?.phone,
                            primaryAuthMethod = methods.name,
                            isTwoFactorEnabled = isTwoFactorEnabled,
                            city = inferredCity,
                            country = countryName,
                            countryCode = countryCode,
                            timezone = userTimezone,
                            loginTimestamp = kotlin.js.Date.now().toLong()
                        )

                        LocalStorageManager.saveUserProfile(profile)

                        Response(true,"login reussi")
                    }
                }

                HttpStatusCode.Unauthorized -> {
                    Response(false,"Email/téléphone ou mot de passe incorrect")
                }

                HttpStatusCode.TooManyRequests -> {
                    Response(false,"Trop de tentatives. Veuillez réessayer plus tard")
                }

                HttpStatusCode.BadRequest -> {
                    val body = response.body<Map<String, String>>()
                    Response(false,body["error"] ?: "Utilisateur déjà existant")
                }

                else -> {
                    Response(false,"Erreur serveur: ${response.status.value}")
                }
            }
        } catch (e: Exception) {
            Response(false,"Erreur de connexion")
        }
    }

    suspend fun nativeLoginFetch(url: String, body: String): Int {
        val dispatch = window.fetch(url, RequestInit(
            method = "POST",
            headers = json("Content-Type" to "application/json"),
            body = body,
            // On force l'option qui a fait gagner ton test en console
            credentials = RequestCredentials.INCLUDE
        )).await()

        return dispatch.status.toInt()
    }

    private fun serializerFor(request: Any) = when(request) {
        is LoginWithEmail -> LoginWithEmail.serializer()
        // ajoute tes autres cas
        else -> throw Exception("Unknown request type")
    }

    suspend fun verify(request: Any, model: LoginMethod) : Response {
        val response: HttpResponse = client.post("$API_AUTH_URL${Endpoint.Verify.path}?method=${model.name}") {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(request)
        }

        val responseText = response.bodyAsText()
        return when (response.status) {
            HttpStatusCode.OK,
            HttpStatusCode.Created,
            HttpStatusCode.Accepted,
            HttpStatusCode.NoContent -> {
                Response(success = true, message = "verification reussi")
            }
            else -> {
                val res = Json.decodeFromString<Error>(responseText)
                Response(success = false, message = res.error)
            }
        }
    }

    suspend fun confirm(request: Any,model: LoginMethod) : Response {
        val response: HttpResponse = client.post("$API_AUTH_URL${Endpoint.Confirm.path}?method=${model.name}") {
            contentType(ContentType.Application.Json)
            accept(ContentType.Application.Json)
            setBody(request)
        }

        val responseText = response.bodyAsText()

        return when (response.status) {
            HttpStatusCode.OK,
            HttpStatusCode.Created,
            HttpStatusCode.Accepted,
            HttpStatusCode.NoContent -> {
                val res = Json.decodeFromString<Success>(responseText)
                Response(success = true, message = res.message)
            }
            else -> {
                val res = Json.decodeFromString<Error>(responseText)
                Response(success = false, message = res.error)
            }
        }

    }

    suspend fun initiatePasswordReset(identity: String,model: LoginMethod): Response {
        return try {
            val response = client.post("$API_AUTH_URL${Endpoint.ForgotPassword.path}?method=${model.name}") {
                contentType(ContentType.Application.Json)
                accept(ContentType.Application.Json)
                setBody(ForgotPasswordRequest(identity = identity))

            }
            val responseText = response.bodyAsText()
            when (response.status) {
                HttpStatusCode.OK -> {
                    Response(success = true, message = "code envoyer avec success")
                }
                HttpStatusCode.BadRequest,
                HttpStatusCode.Unauthorized,
                HttpStatusCode.TooManyRequests,
                HttpStatusCode.InternalServerError -> {
                    val res = Json.decodeFromString<Error>(responseText)
                    Response(success = false, message = res.error)
                }

                else -> {
                    val res = Json.decodeFromString<Error>(responseText)
                    Response(success = false, message = res.error)
                }
            }
        } catch (e: Exception) {
            Response(success = false, message = "erreur de survenu")
        }
    }

    suspend fun resetPassword(request: ResetPasswordRequest,model: LoginMethod): Boolean {
        return try {
            val endpoint = Endpoint.ResetPassword.path

            val response = client.post("$API_AUTH_URL${Endpoint.ResetPassword.path}?method=${model.name}") {
                contentType(ContentType.Application.Json)
                accept(ContentType.Application.Json)
                setBody(request)
            }

            when (response.status) {
                HttpStatusCode.OK -> true

                HttpStatusCode.BadRequest,
                HttpStatusCode.Unauthorized,
                HttpStatusCode.TooManyRequests,
                HttpStatusCode.InternalServerError -> false

                else -> false
            }
        } catch (e: Exception) {
            false
        }
    }

    /**
     * Déconnexion de l'utilisateur
     * Envoie le token JWT au backend pour le révoquer
     */
    suspend fun logout() : Boolean? {
        return try {
            val response = client.get("$API_AUTH_URL${Endpoint.Logout.path}") {
                contentType(ContentType.Application.Json)
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    true
                }

                HttpStatusCode.BadRequest -> {
                    val body = try {
                        response.body<AuthResponse.Error>()
                    } catch (e: Exception) {
                        false
                    }
                    false
                }

                HttpStatusCode.InternalServerError -> {
                    val body = try {
                        response.body<AuthResponse.Error>()
                    } catch (e: Exception) {
                        Response(false,"Échec de la déconnexion")
                    }
                    false
                }

                else -> {
                    Response(false,"Erreur serveur: ${response.status.value}")
                    false
                }
            }
        } catch (e: Exception) {
            Response(false,"Erreur de connexion: ${e.message}")
            false
        }
    }

    suspend fun logoutLocal(service: String, userUuid: String): Boolean {
        return try {
            val response = client.get("$API_AUTH_URL${Endpoint.LogoutLocal.path}") {
                url {
                    parameters.append("service", service)
                    parameters.append("userUuid", userUuid)
                }
                accept(ContentType.Application.Json)
            }

            response.status == HttpStatusCode.OK
        } catch (e: CancellationException) {
            throw e // Relance l'annulation pour ne pas bloquer les coroutines
        } catch (e: Exception) {
            false
        }
    }

//    fun logout() {
//        val redirect = encodeURIComponent("https://cv.site.local")
//
//        window.location.href =
//            "https://account.site.local/auth/logout?service=CV&redirect=$redirect"
//    }

    suspend inline fun <T> safeRequest(block: () -> HttpResponse): Response {
        return try {
            val response = block()

            if (response.status == HttpStatusCode.OK) {
                Response(true,"")
            } else {
                Response(false, "Erreur ${response.status.value}")
            }

        } catch (e: Exception) {
            Response(false, e.message.toString())
        }
    }

    /**
     * Rafraîchir le token d'accès
     */
    suspend fun refreshToken(refreshToken: String): Result<LoginResponse> {
        return try {

            val response = client.post("$API_AUTH_URL${Endpoint.RefreshToken.path}") {
                contentType(ContentType.Application.Json)
                headers {
                    append("Authorization", "Bearer $refreshToken")
                }
            }

            when (response.status) {
                HttpStatusCode.OK -> {
                    val loginResponse = response.body<LoginResponse>()
                    Result.success(loginResponse)
                }

                HttpStatusCode.Unauthorized -> {
                    Result.failure(Exception("Session expirée. Veuillez vous reconnecter"))
                }

                else -> {
                    Result.failure(Exception("Erreur serveur: ${response.status.value}"))
                }
            }
        } catch (e: Exception) {
            Result.failure(Exception("Erreur de connexion: ${e.message}"))
        }
    }

}